
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import ConsumerHeader from "@/components/ConsumerHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Mail, Phone, MessageCircle, ArrowRight, Clock, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface SupportFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
  priority: string;
}

const Support = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const form = useForm<SupportFormData>({
    defaultValues: {
      name: "",
      email: "",
      subject: "",
      message: "",
      priority: "medium",
    },
  });

  useEffect(() => {
    // Initialize Crisp chat (hidden by default)
    (window as any).$crisp = [];
    (window as any).CRISP_WEBSITE_ID = "292c3d15-e80d-4229-9c3e-d92625d98217";
    
    // Hide chatbox on load
    (window as any).$crisp.push(["do", "chat:hide"]);
    
    const script = document.createElement("script");
    script.src = "https://client.crisp.chat/l.js";
    script.async = true;
    document.getElementsByTagName("head")[0].appendChild(script);

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src="https://client.crisp.chat/l.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  const openCrispChat = () => {
    if ((window as any).$crisp) {
      (window as any).$crisp.push(["do", "chat:show"]);
      (window as any).$crisp.push(["do", "chat:open"]);
    }
  };

  const onSubmit = async (data: SupportFormData) => {
    setIsSubmitting(true);
    
    try {
      console.log("Submitting support request:", data);
      
      const { data: result, error } = await supabase.functions.invoke('send-contact-email', {
        body: {
          ...data,
          subject: `[SUPPORT] ${data.subject}`,
          message: `Priority: ${data.priority.toUpperCase()}\n\n${data.message}`
        }
      });

      if (error) {
        throw error;
      }

      console.log("Support request submitted successfully:", result);
      toast.success("Support request sent successfully! We'll get back to you within 24 hours.");
      form.reset();
    } catch (error) {
      console.error("Failed to send support request:", error);
      toast.error("Failed to send support request. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <ConsumerHeader />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            How can we help you?
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Get in touch with our support team. We're here to help you with any questions or issues you may have.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {/* Contact Methods */}
          <div className="space-y-6">
            <Card className="border border-gray-200 shadow-sm">
              <CardHeader className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="h-6 w-6 text-blue-600" />
                </div>
                <CardTitle className="text-lg">Call Us</CardTitle>
                <CardDescription>
                  Speak directly with our support team
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-2xl font-bold text-gray-900 mb-2">+44 20 8050 0395</p>
                <p className="text-sm text-gray-500 mb-4">Monday - Sunday, 7 AM - 10 PM GMT</p>
                <Button className="w-full" variant="outline">
                  Call Now
                </Button>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 shadow-sm">
              <CardHeader className="text-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="h-6 w-6 text-green-600" />
                </div>
                <CardTitle className="text-lg">Live Chat</CardTitle>
                <CardDescription>
                  Get instant help from our team
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-sm text-gray-600 mb-4">Available during business hours</p>
                <Button className="w-full" variant="outline" onClick={openCrispChat}>
                  Start Chat
                </Button>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 shadow-sm">
              <CardHeader className="text-center">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-6 w-6 text-purple-600" />
                </div>
                <CardTitle className="text-lg">Email Support</CardTitle>
                <CardDescription>
                  Send us a detailed message
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-lg font-semibold text-gray-900 mb-2">support@wellsuites.co</p>
                <p className="text-sm text-gray-500 mb-4">Response within 24 hours</p>
              </CardContent>
            </Card>
          </div>

          {/* Support Form */}
          <div className="lg:col-span-2">
            <Card className="border border-gray-200 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <HelpCircle className="h-5 w-5 mr-2" />
                  Submit a Support Request
                </CardTitle>
                <CardDescription>
                  Fill out the form below and we'll get back to you as soon as possible
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="name"
                        rules={{ required: "Name is required" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Your full name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="email"
                        rules={{ 
                          required: "Email is required",
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address"
                          }
                        }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <Input placeholder="your.email@example.com" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="subject"
                        rules={{ required: "Subject is required" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Subject</FormLabel>
                            <FormControl>
                              <Input placeholder="Brief description of your issue" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="priority"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Priority</FormLabel>
                            <FormControl>
                              <select 
                                {...field} 
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                              >
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                                <option value="urgent">Urgent</option>
                              </select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    
                    <FormField
                      control={form.control}
                      name="message"
                      rules={{ required: "Message is required" }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Message</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Please describe your issue in detail..."
                              className="min-h-[120px]"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <Button 
                      type="submit" 
                      size="lg"
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Sending..." : "Send Support Request"}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* FAQ Section */}
        <Card className="border border-gray-200 shadow-sm">
          <CardHeader>
            <CardTitle>Frequently Asked Questions</CardTitle>
            <CardDescription>
              Quick answers to common questions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">How do I book a session?</h3>
                <p className="text-gray-600 text-sm mb-4">
                  You can book sessions through our platform by searching for providers, selecting your preferred time slot, and completing the booking process.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">How do I cancel a booking?</h3>
                <p className="text-gray-600 text-sm mb-4">
                  You can cancel bookings from your dashboard. Please note our cancellation policy may apply depending on the timing.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">What payment methods do you accept?</h3>
                <p className="text-gray-600 text-sm mb-4">
                  We accept all major credit cards, debit cards, and digital payment methods through our secure payment system.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">How do I contact my provider?</h3>
                <p className="text-gray-600 text-sm mb-4">
                  You can find provider contact information in your booking confirmation or through the provider's profile page.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Business Hours */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center bg-blue-50 rounded-lg px-4 py-2">
            <Clock className="h-5 w-5 text-blue-600 mr-2" />
            <span className="text-blue-800 font-medium">
              Support Hours: Monday - Sunday, 7:00 AM - 10:00 PM GMT
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Support;
